import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import eventMapImage from "@/assets/event-map.png";
import { Mail, MessageSquare, Anchor } from "lucide-react";
import { saveEmail, savePhoneNumber, n8nSavePhoneSlickText, saveBoatSlip } from "@/lib/firebase";
import { getValidationErrors } from "@/lib/validation";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView, useMobileOnceViewport } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
const images = {
  hero1: PHOTOS.DJ.behindWithCrowd,
  hero2: PHOTOS.DANCING.womenCheering,
  content1: PHOTOS.DJ.djKeo,
  content2: PHOTOS.BAR.threePeopleCaptainOutfits,
  content3: PHOTOS.DJ.nicNasty
};
const Guide = () => {
  usePageSEO({
    title: "Festival Guide — Yacht Rock Detroit 2026",
    description: "Everything you need to know: venue info, food and drinks, what to bring, and how to make the most of your weekend.",
    keywords: "yacht rock detroit guide, festival guide, roostertail venue, what to bring festival",
    ogImage: PHOTOS.VENUE.roostertailExteriorNight,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Guide", url: "https://yachtrockdetroit.com/guide" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const contentRef = useRef(null);
  const isHeaderInView = useMobileOnceInView(headerRef, {
    margin: "-100px"
  });
  const isContentInView = useMobileOnceInView(contentRef, {
    margin: "-100px"
  });
  const mobileOnceViewport = useMobileOnceViewport("-100px");

  // Signup form state
  const [signupMode, setSignupMode] = useState<"email" | "sms" | null>(null);
  const [signupValue, setSignupValue] = useState("");
  const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState<string | null>(null);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);
  const [hasSubmittedPhone, setHasSubmittedPhone] = useState(false);

  // Boat slip reservation form state
  const [boat, setBoat] = useState({
    hasPasses: "",
    hasAdmiralPass: "",
    partySize: "",
    boatSize: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [boatErrors, setBoatErrors] = useState<Record<string, string>>({});
  const [isBoatSubmitting, setIsBoatSubmitting] = useState(false);
  const [boatSubmitted, setBoatSubmitted] = useState(false);
  const setBoatField = (field: string, value: string) => {
    setBoat(prev => ({ ...prev, [field]: value }));
    setBoatErrors(prev => {
      if (!prev[field] && !prev.form) return prev;
      const next = { ...prev };
      delete next[field];
      delete next.form;
      return next;
    });
  };

  // Check localStorage on mount for signup state
  useEffect(() => {
    const emailSubmitted = localStorage.getItem("yrd_email_submitted") === "true";
    const phoneSubmitted = localStorage.getItem("yrd_phone_submitted") === "true";
    setHasSubmittedEmail(emailSubmitted);
    setHasSubmittedPhone(phoneSubmitted);
  }, []);

  // Scroll to top on mount - instant to prevent flash.
  // If arriving with a hash (e.g. /guide#bring-your-boat from /parking), go there instead.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
      });
      return;
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' as ScrollBehavior
    });
  }, []);
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signupValue) return;
    setIsSignupSubmitting(true);
    setSignupSuccess(null);
    try {
      if (signupMode === "email") {
        await saveEmail(signupValue, "stay-in-know");
        localStorage.setItem("yrd_email_submitted", "true");
        setHasSubmittedEmail(true);
        if (hasSubmittedPhone) {
          setSignupSuccess("You're all set! Thanks for signing up.");
          setSignupMode(null);
        } else {
          setSignupSuccess("Email submitted! Now, what's your phone number?");
          setSignupMode("sms");
        }
        setSignupValue("");
      } else {
        await Promise.all([
          savePhoneNumber(signupValue, "stay-in-know"),
          n8nSavePhoneSlickText(signupValue).catch(console.error),
        ]);
        localStorage.setItem("yrd_phone_submitted", "true");
        setHasSubmittedPhone(true);
        if (hasSubmittedEmail) {
          setSignupSuccess("You're all set! Thanks for signing up.");
          setSignupMode(null);
        } else {
          setSignupSuccess("Phone submitted! Now, what's your email?");
          setSignupMode("email");
        }
        setSignupValue("");
      }
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSignupSubmitting(false);
    }
  };
  const handleBoatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBoatErrors({});
    const payload = {
      firstName: boat.firstName.trim(),
      lastName: boat.lastName.trim(),
      email: boat.email.trim(),
      phone: boat.phone.trim(),
      hasPasses: boat.hasPasses === "yes",
      hasAdmiralPass: boat.hasAdmiralPass === "yes",
      partySize: parseInt(boat.partySize, 10),
      boatSize: boat.boatSize.trim(),
    };
    // Lightweight pre-validation for the radio/number fields the schema can't message well
    const localErrors: Record<string, string> = {};
    if (boat.hasPasses === "") localErrors.hasPasses = "Please select one";
    if (boat.hasAdmiralPass === "") localErrors.hasAdmiralPass = "Please select one";
    if (!boat.partySize || isNaN(payload.partySize)) localErrors.partySize = "How many people?";
    if (Object.keys(localErrors).length) {
      setBoatErrors(localErrors);
      return;
    }
    setIsBoatSubmitting(true);
    try {
      await saveBoatSlip(payload);
      setBoatSubmitted(true);
    } catch (error: any) {
      console.error("Error saving boat slip:", error);
      if (error?.errors) {
        const fieldErrors: Record<string, string> = {};
        getValidationErrors(error).forEach(({ field, message }) => {
          fieldErrors[field] = message;
        });
        setBoatErrors(fieldErrors);
      } else {
        setBoatErrors({ form: "Something went wrong. Please try again." });
      }
    } finally {
      setIsBoatSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* Hero Header Section - Blue with Noise */}
      <section ref={headerRef} className="relative min-h-fit md:min-h-[80vh] bg-yacht-blue overflow-hidden flex flex-col md:flex-row md:items-end">
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />

        {/* Boats graphic background */}
        <img src={boatsGraphic} alt="" className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0" style={{
        opacity: 0.15,
        filter: 'brightness(0) invert(1)'
      }} />

        {/* MOBILE LAYOUT - Centered title + overlapping images below */}
        <div className="md:hidden relative z-20 flex flex-col items-center pt-28 pb-8 px-6">
          {/* Title centered */}
          <motion.p className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={isHeaderInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.6
        }}>
            Know Before You Go
          </motion.p>
          <div className="text-[7vw] font-bold text-white leading-[0.9] tracking-tight text-center mb-8" role="heading" aria-level={1}>
            <GradualSpacing text="Your Guide to" duration={0.5} delayMultiple={0.04} />
            <br />
            <GradualSpacing text="Yacht Rock Detroit 2026" duration={0.5} delayMultiple={0.04} />
          </div>
          
          {/* Images overlapping */}
          <div className="relative w-full max-w-xs h-[320px]">
            <motion.div className="absolute left-0 top-0 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10" initial={{
            opacity: 0,
            y: 30,
            rotate: -5
          }} animate={isHeaderInView ? {
            opacity: 1,
            y: 0,
            rotate: -5
          } : {
            opacity: 0,
            y: 30
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }}>
              <img src={images.hero1} alt="DJ performing behind turntables at Yacht Rock Detroit" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div className="absolute right-0 top-8 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20" initial={{
            opacity: 0,
            y: 30,
            rotate: 5
          }} animate={isHeaderInView ? {
            opacity: 1,
            y: 0,
            rotate: 5
          } : {
            opacity: 0,
            y: 30
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }}>
              <img src={images.hero2} alt="Women cheering in the crowd at Yacht Rock Detroit" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>

        {/* DESKTOP LAYOUT - Off-center images with title bottom left */}
        <motion.div className="hidden md:block absolute top-44 right-[5%] w-[35%] max-w-md aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl" initial={{
        opacity: 0,
        y: 50,
        rotate: 3
      }} animate={isHeaderInView ? {
        opacity: 1,
        y: 0,
        rotate: 3
      } : {
        opacity: 0,
        y: 50
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }}>
          <img src={images.hero1} alt="DJ performing behind turntables at Yacht Rock Detroit" className="w-full h-full object-cover" />
        </motion.div>

        <motion.div className="hidden md:block absolute bottom-24 right-[25%] w-[25%] max-w-xs aspect-square rounded-2xl overflow-hidden shadow-2xl z-10" initial={{
        opacity: 0,
        y: 30,
        rotate: -5
      }} animate={isHeaderInView ? {
        opacity: 1,
        y: 0,
        rotate: -5
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }}>
          <img src={images.hero2} alt="Women cheering in the crowd at Yacht Rock Detroit" className="w-full h-full object-cover" />
        </motion.div>

        {/* Big Title - Bottom Left (Desktop only) */}
        <div className="hidden md:block relative z-20 px-8 md:px-16 pb-16 md:pb-24">
          <motion.p className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4" initial={{
          opacity: 0,
          y: 20
        }} animate={isHeaderInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.6
        }}>
            Know Before You Go
          </motion.p>
          <h1 className="text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="Guide" duration={0.5} delayMultiple={0.08} />
          </h1>
        </div>
      </section>

      {/* Yellow Accent Strip */}
      <section className="relative py-8 bg-yacht-yellow overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />
        <div className="relative z-10 px-8 md:px-16">
          <p className="text-yacht-charcoal/80 text-lg md:text-xl font-medium text-center" style={{
          fontFamily: 'Georgia, serif'
        }}>
            Thanks for sailing with us — sign up for 2027 early access and we'll see you on the river.
          </p>
        </div>
      </section>


      {/* The essentials — editorial index, no cards */}
      <section className="py-16 md:py-24 px-6 md:px-16 bg-yacht-cream">
        <div className="max-w-5xl mx-auto">
          <motion.p
            className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-8 md:mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.6 }}
          >
            The Essentials
          </motion.p>
          <div className="border-t border-yacht-charcoal/20">
            {[
              {
                num: "01",
                title: "2027 Early Access",
                blurb: "Be first aboard for 2027 — dates, lineup, and tickets before the public",
                href: "/tickets",
              },
              {
                num: "02",
                title: "2026 Schedule",
                blurb: "How the weekend ran — five stages, noon to midnight",
                href: "/schedule",
              },
              {
                num: "03",
                title: "Parking",
                blurb: "Riverside Marina: free until 2 PM Saturday ($20 after) · Roostertail lots first come, first served",
                href: "/parking",
              },
              {
                num: "04",
                title: "Hotels & Travel",
                blurb: "Partner rates, directions, rideshare",
                href: "/travel",
              },
              {
                num: "05",
                title: "What to Wear",
                blurb: "Outfit inspiration — captain's hats encouraged",
                href: "/lifestyle/blog/what-to-wear-to-a-yacht-rock-festival",
              },
              {
                num: "06",
                title: "Boat Slips",
                blurb: "Sold out — dockage at The Roostertail is fully booked",
                href: "#bring-your-boat",
              },
            ].map((item, i) => (
              <motion.a
                key={item.title}
                href={item.href}
                className="group flex items-baseline gap-5 md:gap-8 py-6 md:py-8 border-b border-yacht-charcoal/20"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={mobileOnceViewport}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <span className="text-yacht-charcoal/35 text-sm md:text-base font-medium tabular-nums">
                  {item.num}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-3xl md:text-5xl font-bold text-yacht-charcoal tracking-tight leading-none transition-colors group-hover:text-yacht-blue">
                    {item.title}
                  </span>
                  <span className="block text-yacht-charcoal/50 text-sm md:text-base mt-2">
                    {item.blurb}
                  </span>
                </span>
                <span
                  className="text-2xl md:text-4xl font-light text-yacht-charcoal/30 transition-all group-hover:text-yacht-blue group-hover:translate-x-1.5 self-center"
                  aria-hidden
                >
                  →
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - First */}
      <section ref={contentRef} className="py-20 md:py-32 px-8 md:px-16 bg-yacht-cream">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Text Content - Left Side */}
            <div className="lg:col-span-5 text-center lg:text-left">
              <motion.p className="text-yacht-charcoal/50 text-sm font-medium tracking-[0.3em] uppercase mb-4" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={mobileOnceViewport} transition={{
              duration: 0.6
            }}>
                Got Questions?
              </motion.p>
              
              <motion.h2 className="text-[22vw] md:text-[14vw] lg:text-[10vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight mb-8" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={mobileOnceViewport} transition={{
              duration: 0.7,
              delay: 0.1
            }}>
                FAQ
              </motion.h2>
              
              <motion.p className="text-yacht-charcoal/60 text-lg md:text-xl leading-relaxed mb-10 max-w-md mx-auto lg:mx-0" style={{
              fontFamily: 'Georgia, serif'
            }} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={mobileOnceViewport} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
                Everything you need to know about Yacht Rock Detroit—tickets, parking, what to bring, and more.
              </motion.p>
              
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={mobileOnceViewport} transition={{
              duration: 0.6,
              delay: 0.3
            }}>
                <Link to="/faq" className="inline-block px-8 py-4 bg-yacht-blue text-white font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg">
                  View All FAQs
                </Link>
              </motion.div>
            </div>
            
            {/* Photos - Right Side (hidden on mobile) */}
            <div className="hidden md:block lg:col-span-7 relative">
              <div className="relative h-[400px] md:h-[500px]">
                {/* Photo 1 - Large, tilted left */}
                <motion.div className="absolute left-0 md:left-[5%] top-0 w-[55%] md:w-[50%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10" initial={{
                opacity: 0,
                y: 50,
                rotate: -6
              }} whileInView={{
                opacity: 1,
                y: 0,
                rotate: -6
              }} viewport={mobileOnceViewport} transition={{
                duration: 0.8,
                delay: 0.2
              }}>
                  <img src={images.content1} alt="DJ Keo performing at Yacht Rock Detroit" className="w-full h-full object-cover" loading="lazy" />
                </motion.div>
                
                {/* Photo 2 - Medium, tilted right */}
                <motion.div className="absolute right-0 md:right-[5%] top-12 w-[50%] md:w-[45%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20" initial={{
                opacity: 0,
                y: 50,
                rotate: 5
              }} whileInView={{
                opacity: 1,
                y: 0,
                rotate: 5
              }} viewport={mobileOnceViewport} transition={{
                duration: 0.8,
                delay: 0.4
              }}>
                  <img src={images.content2} alt="Three people in captain outfits at the bar" className="w-full h-full object-cover" loading="lazy" />
                </motion.div>
                
                {/* Floating yellow accent */}
                <motion.div className="absolute bottom-8 left-[30%] w-20 h-20 md:w-28 md:h-28 bg-yacht-yellow rounded-xl -z-0" initial={{
                opacity: 0,
                scale: 0.8
              }} whileInView={{
                opacity: 0.8,
                scale: 1
              }} viewport={mobileOnceViewport} transition={{
                duration: 0.6,
                delay: 0.5
              }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Food & Drinks Section */}
      <section className="py-20 md:py-32 px-8 md:px-16 bg-yacht-cream overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Big Centered Title */}
          <div className="text-center mb-8 md:mb-12">
            <motion.h2 className="text-[14vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight" initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }}>
              FOOD &
            </motion.h2>
            <motion.h2 className="text-[14vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-blue leading-[0.85] tracking-tight italic" initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.8,
            delay: 0.15,
            ease: [0.16, 1, 0.3, 1]
          }}>
              DRINKS
            </motion.h2>
          </div>

          {/* Description */}
          <motion.p className="text-yacht-charcoal/60 text-lg md:text-xl text-center max-w-2xl mx-auto mb-12 md:mb-16" style={{
          fontFamily: 'Georgia, serif'
        }} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={mobileOnceViewport} transition={{
          duration: 0.6,
          delay: 0.3
        }}>With multiple waterfront bars and walk-up food stations throughout the venue, you can explore, sip, and snack at your own pace. All food and beverages are available for purchase on site.</motion.p>

          {/* Photos Grid */}
          <div className="relative h-[400px] md:h-[500px] max-w-4xl mx-auto pb-8">
            {/* Photo 1 - Left, tilted */}
            <motion.div className="absolute left-0 md:left-[5%] top-0 w-[45%] md:w-[40%] aspect-[3/4] rounded-2xl overflow-hidden shadow-lg z-10" initial={{
            opacity: 0,
            y: 50,
            rotate: -6
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotate: -6
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.8,
            delay: 0.4
          }}>
              <img src={images.content1} alt="DJ Keo spinning at the waterfront" className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
            
            {/* Photo 2 - Center, larger */}
            <motion.div className="absolute left-1/2 -translate-x-1/2 top-8 w-[50%] md:w-[45%] aspect-[3/4] rounded-2xl overflow-hidden shadow-lg z-20" initial={{
            opacity: 0,
            y: 50,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            y: 0,
            scale: 1
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.8,
            delay: 0.5
          }}>
              <img src={images.content2} alt="Guests in captain outfits enjoying drinks" className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
            
            {/* Photo 3 - Right, tilted */}
            <motion.div className="absolute right-0 md:right-[5%] top-0 w-[45%] md:w-[40%] aspect-[3/4] rounded-2xl overflow-hidden shadow-lg z-30" initial={{
            opacity: 0,
            y: 50,
            rotate: 6
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotate: 6
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.8,
            delay: 0.6
          }}>
              <img src={images.content3} alt="DJ Nic Nasty at the turntables" className="w-full h-full object-cover" loading="lazy" />
            </motion.div>
            
            {/* Floating yellow accent */}
            <motion.div className="absolute bottom-4 left-[20%] w-16 h-16 md:w-24 md:h-24 bg-yacht-yellow rounded-xl -z-0" initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 0.7,
            scale: 1
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.6,
            delay: 0.7
          }} />
          </div>
        </div>
      </section>

      {/* Event Map Section */}
      <section className="relative bg-yacht-cream overflow-hidden">
        {/* Big text behind */}
        <div className="absolute inset-x-0 top-4 md:top-24 flex justify-center pointer-events-none select-none z-0">
          <motion.h2 className="text-[15vw] md:text-[12vw] font-bold text-yacht-blue tracking-tight whitespace-nowrap" initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} viewport={mobileOnceViewport}>
            EVENT MAP
          </motion.h2>
        </div>
        
        {/* PNG Image on top */}
        <div className="relative z-10 px-4 md:px-8">
          <motion.img src={eventMapImage} alt="Yacht Rock Detroit Event Map - Aerial view of The Roostertail venue" className="w-full max-w-6xl mx-auto" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={mobileOnceViewport} />
        </div>
        
        {/* Bottom border/divider */}
        <div className="relative h-[20px] bg-yacht-blue mt-0">
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />
        </div>
      </section>

      {/* Bring Your Boat — Slip Reservation */}
      <section id="bring-your-boat" className="relative py-20 md:py-32 px-6 md:px-16 bg-yacht-blue overflow-hidden scroll-mt-20">
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
      }} />

        <div className="relative z-10 max-w-3xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-10 md:mb-12">
            <motion.div className="inline-flex items-center gap-2 text-yacht-yellow text-sm font-medium tracking-[0.3em] uppercase mb-4" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.6
          }}>
              <Anchor className="w-4 h-4" /> Arrive by Water
            </motion.div>
            <motion.h2 className="text-[12vw] md:text-[7vw] lg:text-6xl font-bold text-white leading-[0.9] tracking-tight mb-5" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.7,
            delay: 0.1
          }}>
              Bring Your Boat
            </motion.h2>
            <motion.p className="text-white/80 text-lg md:text-xl leading-relaxed max-w-xl mx-auto" style={{
            fontFamily: 'Georgia, serif'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={mobileOnceViewport} transition={{
            duration: 0.6,
            delay: 0.2
          }}>
              Dockage at The Roostertail is fully booked for the weekend — boat slips are sold out and we're no longer accepting reservations.
            </motion.p>
          </div>

          {/* Form Card */}
          <motion.div className="bg-yacht-cream rounded-3xl shadow-2xl p-6 md:p-10" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={mobileOnceViewport} transition={{
          duration: 0.7,
          delay: 0.2
        }}>
            {true ? <div className="text-center py-10">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yacht-blue/10 mb-5">
                  <Anchor className="w-8 h-8 text-yacht-blue" />
                </div>
                <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-yacht-charcoal text-yacht-yellow text-sm font-bold tracking-[0.2em] uppercase">
                  Sold Out
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-3">Boat slips are sold out</h3>
                <p className="text-yacht-charcoal/70 text-lg max-w-md mx-auto" style={{
              fontFamily: 'Georgia, serif'
            }}>
                  Dockage at The Roostertail is fully booked for the weekend and we're no longer accepting slip reservations. If you're arriving by water, boat slips at the property are no longer available.
                </p>
              </div> : <form onSubmit={handleBoatSubmit} className="space-y-6">
                {/* Yes/No questions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-yacht-charcoal mb-2">Do you already have passes?</label>
                    <div className="flex gap-3">
                      {["yes", "no"].map(v => <button key={v} type="button" onClick={() => setBoatField("hasPasses", v)} className={`flex-1 py-3 rounded-xl font-semibold capitalize transition-all ${boat.hasPasses === v ? "bg-yacht-blue text-white shadow-md" : "bg-white text-yacht-charcoal/70 hover:bg-white/80 border border-yacht-charcoal/10"}`}>{v}</button>)}
                    </div>
                    {boatErrors.hasPasses && <p className="text-red-600 text-sm mt-1">{boatErrors.hasPasses}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-yacht-charcoal mb-2">Do you have an Admiral's (VIP) pass?</label>
                    <div className="flex gap-3">
                      {["yes", "no"].map(v => <button key={v} type="button" onClick={() => setBoatField("hasAdmiralPass", v)} className={`flex-1 py-3 rounded-xl font-semibold capitalize transition-all ${boat.hasAdmiralPass === v ? "bg-yacht-blue text-white shadow-md" : "bg-white text-yacht-charcoal/70 hover:bg-white/80 border border-yacht-charcoal/10"}`}>{v}</button>)}
                    </div>
                    {boatErrors.hasAdmiralPass && <p className="text-red-600 text-sm mt-1">{boatErrors.hasAdmiralPass}</p>}
                  </div>
                </div>

                {/* Party size + boat size */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="boat-party" className="block text-sm font-semibold text-yacht-charcoal mb-2">How many people are you bringing?</label>
                    <input id="boat-party" name="partySize" type="number" min={1} inputMode="numeric" value={boat.partySize} onChange={e => setBoatField("partySize", e.target.value)} placeholder="e.g. 6" className="w-full px-4 py-3 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-blue/40 border border-yacht-charcoal/10" />
                    {boatErrors.partySize && <p className="text-red-600 text-sm mt-1">{boatErrors.partySize}</p>}
                  </div>
                  <div>
                    <label htmlFor="boat-size" className="block text-sm font-semibold text-yacht-charcoal mb-2">Size of your boat</label>
                    <input id="boat-size" name="boatSize" type="text" value={boat.boatSize} onChange={e => setBoatField("boatSize", e.target.value)} placeholder="e.g. 32 ft" className="w-full px-4 py-3 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-blue/40 border border-yacht-charcoal/10" />
                    {boatErrors.boatSize && <p className="text-red-600 text-sm mt-1">{boatErrors.boatSize}</p>}
                  </div>
                </div>

                {/* Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="boat-first" className="block text-sm font-semibold text-yacht-charcoal mb-2">First name</label>
                    <input id="boat-first" name="firstName" type="text" autoComplete="given-name" value={boat.firstName} onChange={e => setBoatField("firstName", e.target.value)} placeholder="First name" className="w-full px-4 py-3 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-blue/40 border border-yacht-charcoal/10" />
                    {boatErrors.firstName && <p className="text-red-600 text-sm mt-1">{boatErrors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="boat-last" className="block text-sm font-semibold text-yacht-charcoal mb-2">Last name</label>
                    <input id="boat-last" name="lastName" type="text" autoComplete="family-name" value={boat.lastName} onChange={e => setBoatField("lastName", e.target.value)} placeholder="Last name" className="w-full px-4 py-3 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-blue/40 border border-yacht-charcoal/10" />
                    {boatErrors.lastName && <p className="text-red-600 text-sm mt-1">{boatErrors.lastName}</p>}
                  </div>
                </div>

                {/* Email + phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="boat-email" className="block text-sm font-semibold text-yacht-charcoal mb-2">Email</label>
                    <input id="boat-email" name="email" type="email" autoComplete="email" inputMode="email" value={boat.email} onChange={e => setBoatField("email", e.target.value)} placeholder="your@email.com" className="w-full px-4 py-3 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-blue/40 border border-yacht-charcoal/10" />
                    {boatErrors.email && <p className="text-red-600 text-sm mt-1">{boatErrors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="boat-phone" className="block text-sm font-semibold text-yacht-charcoal mb-2">Phone number</label>
                    <input id="boat-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={boat.phone} onChange={e => setBoatField("phone", e.target.value)} placeholder="(313) 555-0123" className="w-full px-4 py-3 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-blue/40 border border-yacht-charcoal/10" />
                    {boatErrors.phone && <p className="text-red-600 text-sm mt-1">{boatErrors.phone}</p>}
                  </div>
                </div>

                {boatErrors.form && <p className="text-red-600 text-sm text-center">{boatErrors.form}</p>}

                <button type="submit" disabled={isBoatSubmitting} className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-yacht-charcoal text-white font-bold text-lg rounded-full hover:bg-yacht-charcoal/90 transition-all duration-300 disabled:opacity-50 shadow-lg">
                  <Anchor className="w-5 h-5" />
                  {isBoatSubmitting ? "Sending…" : "Reserve a Boat Slip"}
                </button>
              </form>}
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-yacht-yellow">
        <div className="max-w-4xl mx-auto text-center px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-yacht-charcoal mb-6">
            Ready to rock?
          </h2>
          <p className="text-yacht-charcoal/70 text-xl mb-8" style={{
          fontFamily: 'Georgia, serif'
        }}>
            Sign up and we'll see you on the waterfront.
          </p>
          
          {/* Signup Form */}
          {!signupMode ? hasSubmittedEmail && hasSubmittedPhone ? <p className="text-green-700 font-semibold text-lg">You're already signed up! We'll keep you posted.</p> : <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {!hasSubmittedEmail && <button onClick={() => setSignupMode("email")} className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-yacht-charcoal text-white font-semibold text-lg rounded-full hover:bg-yacht-charcoal/90 transition-all duration-300 hover:scale-105 shadow-lg">
                    <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    Sign up via Email
                  </button>}
                {!hasSubmittedPhone && <button onClick={() => setSignupMode("sms")} className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-yacht-blue text-white font-semibold text-lg rounded-full hover:bg-yacht-blue/90 transition-all duration-300 hover:scale-105 shadow-lg">
                    <MessageSquare className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    Sign up via SMS
                  </button>}
              </div> : <form onSubmit={handleSignupSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  {signupMode === "email" ? <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" /> : <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />}
                  <input type={signupMode === "email" ? "email" : "tel"} id={signupMode === "email" ? "guide-email" : "guide-phone"} name={signupMode === "email" ? "email" : "phone"} autoComplete={signupMode === "email" ? "email" : "tel"} inputMode={signupMode === "email" ? "email" : "tel"} placeholder={signupMode === "email" ? "your@email.com" : "Your phone number"} value={signupValue} onChange={e => setSignupValue(e.target.value)} required className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-charcoal/20 text-lg shadow-lg" />
                </div>
                <button type="submit" disabled={isSignupSubmitting} className="px-8 py-4 bg-yacht-charcoal text-white font-semibold text-lg rounded-full hover:bg-yacht-charcoal/90 transition-all duration-300 disabled:opacity-50 shadow-lg">
                  {isSignupSubmitting ? "..." : "Submit"}
                </button>
              </div>
              <div className="mt-4 flex flex-col items-center gap-2">
                {signupSuccess && <p className="text-green-700 font-medium">{signupSuccess}</p>}
                <button type="button" onClick={() => {
              setSignupMode(signupMode === "email" ? "sms" : "email");
              setSignupValue("");
              setSignupSuccess(null);
            }} className="text-yacht-charcoal/60 hover:text-yacht-charcoal underline text-sm transition-colors">
                  {signupMode === "email" ? "Switch to SMS →" : "← Switch to Email"}
                </button>
              </div>
            </form>}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>;
};
export default Guide;